import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import axiosInstance from "../../axiosInstance";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { getJobPortalAuthConfig } from "../../helpers/jobPortalAuth";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
);

const numberFormat = new Intl.NumberFormat("en-IN");

const formatNumber = (value) => numberFormat.format(Number(value) || 0);

const percent = (value, total) => {
  if (!total) return 0;
  return Math.round(((Number(value) || 0) / total) * 100);
};

const getInitials = (name = "YNB") =>
  String(name)
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const statusClass = {
  approved: "success",
  active: "success",
  shortlisted: "warning",
  selected: "primary",
  rejected: "danger",
  pending: "warning",
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    stats: {},
    recentRecruiters: [],
    recentJobSeekers: [],
    recentJobs: [],
    recentApplications: [],
    pendingApprovalJobs: [],
  });

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.get(
        "/job-portal/admin/dashboard",
        authConfig
      );
      setData(response.data?.data || {});
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load admin dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const stats = data.stats || {};

  const totals = useMemo(
    () => ({
      users: Number(stats.totalUsers) || 0,
      recruiters: Number(stats.totalRecruiters) || 0,
      seekers: Number(stats.totalJobSeekers) || 0,
      jobs: Number(stats.totalJobs) || 0,
      activeJobs: Number(stats.activeJobs) || 0,
      pendingJobs: Number(stats.pendingApprovalJobs) || 0,
      approvedJobs: Number(stats.approvedJobs) || 0,
      rejectedJobs: Number(stats.rejectedJobs) || 0,
      applications: Number(stats.totalApplications) || 0,
      shortlisted: Number(stats.shortlistedApplications) || 0,
      rejectedApplications: Number(stats.rejectedApplications) || 0,
      selected: Number(stats.selectedApplications) || 0,
      subscriptions: Number(stats.totalSubscriptionOrders) || 0,
      paidSubscriptions: Number(stats.paidSubscriptionOrders) || 0,
      recruiterSubscriptions: Number(stats.recruiterSubscriptionOrders) || 0,
      seekerSubscriptions: Number(stats.jobSeekerSubscriptionOrders) || 0,
      newRecruitersThisMonth: Number(stats.newRecruitersThisMonth) || 0,
      newJobSeekersThisMonth: Number(stats.newJobSeekersThisMonth) || 0,
      jobsPostedThisMonth: Number(stats.jobsPostedThisMonth) || 0,
      applicationsThisMonth: Number(stats.applicationsThisMonth) || 0,
    }),
    [stats]
  );

  const kpis = [
    {
      label: "Total Users",
      value: totals.users,
      trend: `${formatNumber(totals.recruiters)} recruiters / ${formatNumber(totals.seekers)} seekers`,
      icon: "bi-people-fill",
      tone: "blue",
      to: "/all-user",
    },
    {
      label: "Active Jobs",
      value: totals.activeJobs,
      trend: `${percent(totals.activeJobs, totals.jobs)}% of all posted jobs`,
      icon: "bi-briefcase-fill",
      tone: "green",
      to: "/all-job",
    },
    {
      label: "Applications",
      value: totals.applications,
      trend: `${formatNumber(totals.applicationsThisMonth)} received this month`,
      icon: "bi-send-check-fill",
      tone: "purple",
      to: "/admin/applications",
    },
    {
      label: "Paid Orders",
      value: totals.paidSubscriptions,
      trend: `${percent(totals.paidSubscriptions, totals.subscriptions)}% paid conversion`,
      icon: "bi-credit-card-fill",
      tone: "orange",
      to: "/admin/subscription-orders",
    },
  ];

  const chartBaseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          color: "#64748b",
          font: { size: 12, weight: "600" },
          usePointStyle: true,
        },
      },
      tooltip: {
        backgroundColor: "#111827",
        borderColor: "rgba(255,255,255,0.12)",
        borderWidth: 1,
        padding: 12,
        titleColor: "#ffffff",
        bodyColor: "#d1d5db",
      },
    },
  };

  const funnelData = {
    labels: ["Recruiters", "Job Seekers", "Jobs", "Applications"],
    datasets: [
      {
        label: "Platform volume",
        data: [totals.recruiters, totals.seekers, totals.jobs, totals.applications],
        borderRadius: 8,
        backgroundColor: ["#2563eb", "#14b8a6", "#f97316", "#7c3aed"],
        barThickness: 32,
      },
    ],
  };

  const jobHealthData = {
    labels: ["Active", "Pending", "Approved", "Rejected"],
    datasets: [
      {
        data: [
          totals.activeJobs,
          totals.pendingJobs,
          totals.approvedJobs,
          totals.rejectedJobs,
        ],
        backgroundColor: ["#16a34a", "#f59e0b", "#2563eb", "#ef4444"],
        borderColor: "#ffffff",
        borderWidth: 4,
        hoverOffset: 8,
      },
    ],
  };

  const applicationData = {
    labels: ["Total", "Shortlisted", "Selected", "Rejected"],
    datasets: [
      {
        label: "Applications",
        data: [
          totals.applications,
          totals.shortlisted,
          totals.selected,
          totals.rejectedApplications,
        ],
        borderColor: "#2563eb",
        backgroundColor: "rgba(37, 99, 235, 0.12)",
        pointBackgroundColor: "#2563eb",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        tension: 0.38,
        fill: true,
      },
    ],
  };

  const monthData = [
    { label: "New recruiters", value: totals.newRecruitersThisMonth, icon: "bi-building-add" },
    { label: "New seekers", value: totals.newJobSeekersThisMonth, icon: "bi-person-plus" },
    { label: "Jobs posted", value: totals.jobsPostedThisMonth, icon: "bi-briefcase" },
    { label: "Applications", value: totals.applicationsThisMonth, icon: "bi-send" },
  ];

  const userMix = [
    { label: "Recruiters", value: totals.recruiters, total: totals.users, color: "#2563eb" },
    { label: "Job Seekers", value: totals.seekers, total: totals.users, color: "#14b8a6" },
  ];

  const subscriptionMix = [
    {
      label: "Recruiter subscriptions",
      value: totals.recruiterSubscriptions,
      total: totals.subscriptions,
      color: "#7c3aed",
    },
    {
      label: "Job seeker subscriptions",
      value: totals.seekerSubscriptions,
      total: totals.subscriptions,
      color: "#f97316",
    },
  ];

  const renderMiniList = (items, emptyText, type) => {
    if (loading) {
      return (
        <div className="admin-dash-loading">
          <span className="spinner-border spinner-border-sm me-2" />
          Loading
        </div>
      );
    }

    if (!items?.length) {
      return <div className="admin-dash-empty">{emptyText}</div>;
    }

    return items.slice(0, 5).map((item) => {
      const title = type === "user" ? item.name : item.title;
      const subtitle =
        type === "user"
          ? `${item.email || "-"} | ${formatNumber(item.counts?.totalJobs || item.counts?.totalApplications || 0)} activity`
          : `${item.company?.name || "-"} | ${item.location?.display || item.recruiter?.name || "-"}`;

      return (
        <div className="admin-dash-list-item" key={item.id || title}>
          <div className="admin-dash-avatar">{getInitials(title)}</div>
          <div className="min-w-0">
            <div className="admin-dash-list-title">{title || "-"}</div>
            <div className="admin-dash-list-subtitle">{subtitle}</div>
          </div>
        </div>
      );
    });
  };

  return (
    <>
      <style>{`
        .admin-dash {
          background: #f6f8fb;
          margin-left: -0.75rem;
          margin-right: -0.75rem;
          min-height: calc(100vh - 72px);
          padding: 24px;
        }

        .admin-dash-hero {
          align-items: stretch;
          background:
            linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 64, 175, 0.9)),
            radial-gradient(circle at 80% 10%, rgba(20, 184, 166, 0.45), transparent 34%);
          border-radius: 8px;
          color: #ffffff;
          display: grid;
          gap: 20px;
          grid-template-columns: minmax(0, 1fr) auto;
          margin-bottom: 20px;
          overflow: hidden;
          padding: 28px;
          position: relative;
        }

        .admin-dash-hero-title {
          font-size: clamp(26px, 3vw, 38px);
          font-weight: 800;
          letter-spacing: 0;
          line-height: 1.1;
          margin: 0 0 10px;
        }

        .admin-dash-hero-copy {
          color: rgba(255, 255, 255, 0.72);
          font-size: 14px;
          line-height: 1.65;
          margin: 0;
          max-width: 680px;
        }

        .admin-dash-actions {
          align-items: flex-end;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: flex-end;
        }

        .admin-dash-actions .btn {
          border-radius: 8px;
          font-weight: 700;
          min-height: 40px;
        }

        .admin-dash-kpi {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
          color: #0f172a;
          display: block;
          height: 100%;
          overflow: hidden;
          padding: 18px;
          text-decoration: none;
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }

        .admin-dash-kpi:hover {
          box-shadow: 0 18px 42px rgba(15, 23, 42, 0.1);
          color: #0f172a;
          transform: translateY(-3px);
        }

        .admin-dash-kpi-top {
          align-items: center;
          display: flex;
          justify-content: space-between;
          margin-bottom: 18px;
        }

        .admin-dash-icon {
          align-items: center;
          border-radius: 8px;
          display: flex;
          font-size: 18px;
          height: 42px;
          justify-content: center;
          width: 42px;
        }

        .admin-dash-tone-blue { background: #dbeafe; color: #2563eb; }
        .admin-dash-tone-green { background: #dcfce7; color: #16a34a; }
        .admin-dash-tone-purple { background: #ede9fe; color: #7c3aed; }
        .admin-dash-tone-orange { background: #ffedd5; color: #f97316; }

        .admin-dash-kpi-label {
          color: #64748b;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0;
          margin: 0 0 8px;
          text-transform: uppercase;
        }

        .admin-dash-kpi-value {
          font-size: 32px;
          font-weight: 800;
          letter-spacing: 0;
          line-height: 1;
          margin-bottom: 8px;
        }

        .admin-dash-kpi-trend {
          color: #64748b;
          font-size: 13px;
          font-weight: 600;
          overflow-wrap: anywhere;
        }

        .admin-dash-panel {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 14px 34px rgba(15, 23, 42, 0.05);
          height: 100%;
          overflow: hidden;
        }

        .admin-dash-panel-body {
          padding: 20px;
        }

        .admin-dash-panel-head {
          align-items: center;
          border-bottom: 1px solid #eef2f7;
          display: flex;
          gap: 12px;
          justify-content: space-between;
          margin: -20px -20px 18px;
          padding: 16px 20px;
        }

        .admin-dash-panel-title {
          color: #0f172a;
          font-size: 15px;
          font-weight: 800;
          margin: 0;
        }

        .admin-dash-panel-note {
          color: #94a3b8;
          font-size: 12px;
          font-weight: 700;
        }

        .admin-dash-chart {
          height: 292px;
          position: relative;
        }

        .admin-dash-donut {
          height: 260px;
          position: relative;
        }

        .admin-dash-month-card {
          align-items: center;
          background: #f8fafc;
          border: 1px solid #edf2f7;
          border-radius: 8px;
          display: flex;
          gap: 12px;
          padding: 14px;
        }

        .admin-dash-month-icon {
          align-items: center;
          background: #ffffff;
          border-radius: 8px;
          color: #2563eb;
          display: flex;
          flex: 0 0 38px;
          height: 38px;
          justify-content: center;
          width: 38px;
        }

        .admin-dash-month-value {
          color: #0f172a;
          font-size: 22px;
          font-weight: 800;
          line-height: 1;
        }

        .admin-dash-month-label {
          color: #64748b;
          font-size: 12px;
          font-weight: 700;
          margin-top: 4px;
        }

        .admin-dash-progress-row {
          margin-bottom: 17px;
        }

        .admin-dash-progress-row:last-child {
          margin-bottom: 0;
        }

        .admin-dash-progress-meta {
          align-items: center;
          color: #334155;
          display: flex;
          font-size: 13px;
          font-weight: 800;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .admin-dash-track {
          background: #edf2f7;
          border-radius: 999px;
          height: 9px;
          overflow: hidden;
        }

        .admin-dash-fill {
          border-radius: inherit;
          height: 100%;
          min-width: 4px;
        }

        .admin-dash-list-item {
          align-items: center;
          border-bottom: 1px solid #eef2f7;
          display: flex;
          gap: 12px;
          padding: 13px 0;
        }

        .admin-dash-list-item:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .admin-dash-avatar {
          align-items: center;
          background: #e0f2fe;
          border-radius: 8px;
          color: #0369a1;
          display: flex;
          flex: 0 0 42px;
          font-size: 12px;
          font-weight: 800;
          height: 42px;
          justify-content: center;
          width: 42px;
        }

        .admin-dash-list-title {
          color: #0f172a;
          font-size: 14px;
          font-weight: 800;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .admin-dash-list-subtitle {
          color: #64748b;
          font-size: 12px;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .admin-dash-empty,
        .admin-dash-loading {
          align-items: center;
          background: #f8fafc;
          border: 1px dashed #cbd5e1;
          border-radius: 8px;
          color: #64748b;
          display: flex;
          font-size: 13px;
          font-weight: 700;
          justify-content: center;
          min-height: 112px;
          padding: 16px;
          text-align: center;
        }

        .admin-dash-table {
          margin: 0;
        }

        .admin-dash-table th {
          background: #f8fafc;
          border-bottom: 0;
          color: #64748b;
          font-size: 12px;
          font-weight: 800;
          padding: 13px 14px;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .admin-dash-table td {
          border-color: #eef2f7;
          color: #334155;
          font-size: 13px;
          font-weight: 600;
          padding: 14px;
          vertical-align: middle;
        }

        .admin-dash-badge {
          border-radius: 999px;
          display: inline-flex;
          font-size: 11px;
          font-weight: 800;
          padding: 5px 10px;
          text-transform: capitalize;
          white-space: nowrap;
        }

        .admin-dash-badge-success { background: #dcfce7; color: #166534; }
        .admin-dash-badge-warning { background: #fef3c7; color: #92400e; }
        .admin-dash-badge-primary { background: #dbeafe; color: #1d4ed8; }
        .admin-dash-badge-danger { background: #fee2e2; color: #b91c1c; }

        .min-w-0 {
          min-width: 0;
        }

        @media (max-width: 991.98px) {
          .admin-dash-hero {
            grid-template-columns: 1fr;
          }

          .admin-dash-actions {
            justify-content: flex-start;
          }
        }

        @media (max-width: 575.98px) {
          .admin-dash {
            margin-left: -1rem;
            margin-right: -1rem;
            padding: 16px;
          }

          .admin-dash-hero {
            padding: 20px;
          }

          .admin-dash-actions .btn {
            width: 100%;
          }

          .admin-dash-chart,
          .admin-dash-donut {
            height: 240px;
          }
        }
      `}</style>

      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="admin-dash">
          <section className="admin-dash-hero">
            <div>
              <h1 className="admin-dash-hero-title text-white">Admin Job Portal Dashboard</h1>
              <p className="admin-dash-hero-copy">
                Full platform visibility for recruiters, job seekers, jobs, approvals,
                applications, and subscriptions in one operational view.
              </p>
            </div>
            <div className="admin-dash-actions">
              <Link to="/admin/job-approvals" className="btn btn-white">
                <i className="bi bi-check2-square me-2" />
                Review Jobs
              </Link>
              <Link to="/admin/applications" className="btn btn-primary">
                <i className="bi bi-send-check me-2" />
                Applications
              </Link>
              <Link to="/admin/database-transfer" className="btn btn-white">
                <i className="bi bi-database-down me-2" />
                Import / Export
              </Link>
            </div>
          </section>

          <div className="row g-3 mb-4">
            {kpis.map((kpi) => (
              <div className="col-sm-6 col-xxl-3" key={kpi.label}>
                <Link to={kpi.to} className="admin-dash-kpi">
                  <div className="admin-dash-kpi-top">
                    <div className={`admin-dash-icon admin-dash-tone-${kpi.tone}`}>
                      <i className={`bi ${kpi.icon}`} />
                    </div>
                    <i className="bi bi-arrow-up-right text-muted" />
                  </div>
                  <p className="admin-dash-kpi-label">{kpi.label}</p>
                  <div className="admin-dash-kpi-value">
                    {loading ? "--" : formatNumber(kpi.value)}
                  </div>
                  <div className="admin-dash-kpi-trend">{loading ? "Loading snapshot" : kpi.trend}</div>
                </Link>
              </div>
            ))}
          </div>

          <div className="row g-4 mb-4">
            <div className="col-xl-8">
              <section className="admin-dash-panel">
                <div className="admin-dash-panel-body">
                  <div className="admin-dash-panel-head">
                    <div>
                      <h2 className="admin-dash-panel-title">Platform Funnel</h2>
                      <span className="admin-dash-panel-note">Recruiters, seekers, jobs, and applications</span>
                    </div>
                    <Link to="/all-job" className="btn btn-sm btn-outline-primary">
                      View jobs
                    </Link>
                  </div>
                  <div className="admin-dash-chart">
                    <Bar
                      data={funnelData}
                      options={{
                        ...chartBaseOptions,
                        plugins: { ...chartBaseOptions.plugins, legend: { display: false } },
                        scales: {
                          x: {
                            grid: { display: false },
                            ticks: { color: "#64748b", font: { weight: "700" } },
                          },
                          y: {
                            beginAtZero: true,
                            grid: { color: "#eef2f7" },
                            ticks: { precision: 0, color: "#64748b" },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </section>
            </div>

            <div className="col-xl-4">
              <section className="admin-dash-panel">
                <div className="admin-dash-panel-body">
                  <div className="admin-dash-panel-head">
                    <div>
                      <h2 className="admin-dash-panel-title">Job Health</h2>
                      <span className="admin-dash-panel-note">Approval and publishing status</span>
                    </div>
                  </div>
                  <div className="admin-dash-donut">
                    <Doughnut
                      data={jobHealthData}
                      options={{
                        ...chartBaseOptions,
                        cutout: "68%",
                        plugins: {
                          ...chartBaseOptions.plugins,
                          legend: { ...chartBaseOptions.plugins.legend, position: "bottom" },
                        },
                      }}
                    />
                  </div>
                </div>
              </section>
            </div>
          </div>

          <div className="row g-4 mb-4">
            <div className="col-xl-7">
              <section className="admin-dash-panel">
                <div className="admin-dash-panel-body">
                  <div className="admin-dash-panel-head">
                    <div>
                      <h2 className="admin-dash-panel-title">Application Pipeline</h2>
                      <span className="admin-dash-panel-note">Total, shortlisted, selected, and rejected</span>
                    </div>
                  </div>
                  <div className="admin-dash-chart">
                    <Line
                      data={applicationData}
                      options={{
                        ...chartBaseOptions,
                        plugins: { ...chartBaseOptions.plugins, legend: { display: false } },
                        scales: {
                          x: {
                            grid: { display: false },
                            ticks: { color: "#64748b", font: { weight: "700" } },
                          },
                          y: {
                            beginAtZero: true,
                            grid: { color: "#eef2f7" },
                            ticks: { precision: 0, color: "#64748b" },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </section>
            </div>

            <div className="col-xl-5">
              <section className="admin-dash-panel">
                <div className="admin-dash-panel-body">
                  <div className="admin-dash-panel-head">
                    <div>
                      <h2 className="admin-dash-panel-title">This Month</h2>
                      <span className="admin-dash-panel-note">Fresh movement across the portal</span>
                    </div>
                  </div>
                  <div className="row g-3">
                    {monthData.map((item) => (
                      <div className="col-sm-6" key={item.label}>
                        <div className="admin-dash-month-card">
                          <div className="admin-dash-month-icon">
                            <i className={`bi ${item.icon}`} />
                          </div>
                          <div>
                            <div className="admin-dash-month-value">
                              {loading ? "--" : formatNumber(item.value)}
                            </div>
                            <div className="admin-dash-month-label">{item.label}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4">
                    {[...userMix, ...subscriptionMix].map((item) => (
                      <div className="admin-dash-progress-row" key={item.label}>
                        <div className="admin-dash-progress-meta">
                          <span>{item.label}</span>
                          <span>{formatNumber(item.value)}</span>
                        </div>
                        <div className="admin-dash-track">
                          <div
                            className="admin-dash-fill"
                            style={{
                              background: item.color,
                              width: `${Math.max(4, percent(item.value, item.total))}%`,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </div>
          </div>

          <div className="row g-4 mb-4">
            <div className="col-xl-4">
              <section className="admin-dash-panel">
                <div className="admin-dash-panel-body">
                  <div className="admin-dash-panel-head">
                    <div>
                      <h2 className="admin-dash-panel-title">Pending Approval Jobs</h2>
                      <span className="admin-dash-panel-note">{formatNumber(totals.pendingJobs)} waiting</span>
                    </div>
                    <Link to="/admin/job-approvals" className="btn btn-sm btn-outline-primary">
                      Review
                    </Link>
                  </div>
                  {renderMiniList(data.pendingApprovalJobs, "No pending approval jobs.", "job")}
                </div>
              </section>
            </div>

            <div className="col-xl-4">
              <section className="admin-dash-panel">
                <div className="admin-dash-panel-body">
                  <div className="admin-dash-panel-head">
                    <div>
                      <h2 className="admin-dash-panel-title">Recent Jobs</h2>
                      <span className="admin-dash-panel-note">Latest published roles</span>
                    </div>
                  </div>
                  {renderMiniList(data.recentJobs, "No recent jobs.", "job")}
                </div>
              </section>
            </div>

            <div className="col-xl-4">
              <section className="admin-dash-panel">
                <div className="admin-dash-panel-body">
                  <div className="admin-dash-panel-head">
                    <div>
                      <h2 className="admin-dash-panel-title">Recent Users</h2>
                      <span className="admin-dash-panel-note">Recruiters and job seekers</span>
                    </div>
                  </div>
                  {renderMiniList(
                    [...(data.recentRecruiters || []), ...(data.recentJobSeekers || [])].slice(0, 5),
                    "No recent users.",
                    "user"
                  )}
                </div>
              </section>
            </div>
          </div>

          <section className="admin-dash-panel">
            <div className="admin-dash-panel-body">
              <div className="admin-dash-panel-head">
                <div>
                  <h2 className="admin-dash-panel-title">Recent Applications</h2>
                  <span className="admin-dash-panel-note">Newest candidate activity</span>
                </div>
                <Link to="/admin/applications" className="btn btn-sm btn-primary">
                  Open applications
                </Link>
              </div>

              <div className="table-responsive">
                <table className="table admin-dash-table align-middle">
                  <thead>
                    <tr>
                      <th>Applicant</th>
                      <th>Job</th>
                      <th>Recruiter</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">
                          Loading applications...
                        </td>
                      </tr>
                    ) : data.recentApplications?.length ? (
                      data.recentApplications.map((application) => {
                        const status = String(application.statusLabel || "pending").toLowerCase();
                        const tone = statusClass[status] || "primary";

                        return (
                          <tr key={application.id}>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <span className="admin-dash-avatar">
                                  {getInitials(application.applicant?.name)}
                                </span>
                                <span>{application.applicant?.name || "-"}</span>
                              </div>
                            </td>
                            <td>{application.job?.title || "-"}</td>
                            <td>{application.job?.recruiter?.name || "-"}</td>
                            <td>
                              <span className={`admin-dash-badge admin-dash-badge-${tone}`}>
                                {application.statusLabel || "-"}
                              </span>
                            </td>
                            <td>
                              {application.appliedAt
                                ? new Date(application.appliedAt).toLocaleDateString("en-IN", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })
                                : "-"}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">
                          No recent applications.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default Dashboard;
